import { CopywritingStatus } from "@/pages/Private/pages/Messages/components/GenerateBar/types";
import { ListPerson } from "@/pages/Private/pages/List/schema/list";
import { isDummyProspect } from "@/pages/Private/pages/List/utils/columns/dummyProspect";
import { isCreditsRefusalMessage } from "@/pages/Private/redux/copywritingOrchestration/copywritingOrchestration.slice";

/**
 * The GenerateBar props that come from the lead itself: who it writes to, the lead's scoring
 * categories, and its persisted copywriting state. Extracted from `useAddInboxChat` so that hook
 * stays under the file-length gate.
 *
 * `copywritingSince` is the lead's last write: while a generation is queued or running that is
 * the status write, so a reload shows the time spent so far instead of restarting at zero
 * (PREC-4052). Another write to the lead in between makes it read shorter, never longer.
 */
export const generateBarLeadProps = (lead: ListPerson | null | undefined) => ({
	companyCategory:
		lead?.prospect?.organization?.scoreCompanyCategoryResult?.scoreCompanyCategory?.category,
	copywritingStatus: lead?.copywritingStatus,
	copywritingOutput: lead?.copywritingOutput,
	copywritingError: lead?.copywritingError,
	copywritingSince: lead?.updatedAt ?? null,
	listId: lead?.list?.id ?? 0,
	personaCategory: lead?.prospect?.scorePersonaCategoryResult?.scorePersonaCategory?.category,
	prospectCountry: lead?.prospect?.country,
	prospectFirstName: lead?.prospect?.firstName,
	prospectHasEmail: !!lead?.prospect?.email,
	prospectHasLinkedinUrl: !!lead?.prospect?.linkedinUrl,
	prospectId: lead?.prospect?.id ?? 0,
	prospectLanguage: lead?.prospect?.language,
	prospectNotResolved: lead ? isDummyProspect(lead) : false,
	seedSignalId: lead?.trackerSignalResult?.id,
});

/**
 * Whether the lead's stored AI sequence is shown as its drafts. A failed run leaves the previous
 * sequence in place on the server (only the status and the error are written), so those drafts
 * stay on screen with the failure above them instead of disappearing (PREC-4052).
 */
export const keepsAiDrafts = (status: CopywritingStatus | null | undefined): boolean =>
	status === CopywritingStatus.COMPLETED || status === CopywritingStatus.FAILED;

export interface CopywritingFailure {
	message: string;
	isCredits: boolean;
}

/** The failure to show above a lead's drafts, or null when its last generation did not fail. */
export const copywritingFailureOf = (
	lead: Pick<ListPerson, "copywritingStatus" | "copywritingError"> | null | undefined
): CopywritingFailure | null => {
	if (lead?.copywritingStatus !== CopywritingStatus.FAILED || !lead.copywritingError) {
		return null;
	}

	return {
		message: lead.copywritingError,
		isCredits: isCreditsRefusalMessage(lead.copywritingError),
	};
};
